import type { Project, ProjectColor } from '@timato/shared';
import { uuidv7 } from '@timato/shared';
import { db } from '@/db/db';
import { deleteRow, notDeleted, saveRow } from './repo';
import { PRESET_COLORS } from '@/lib/projectColors';

export type ProjectInput = { name: string; color: ProjectColor; weeklyTargetSec: number | null; labelIds: string[] };

/** The least-used named colour among active projects; ties resolve in palette order (tomato first).
 *  Only the presets are suggested - a custom hue is a deliberate choice, never something we hand out. */
export function suggestColor(existing: Pick<Project, 'color'>[]): ProjectColor {
  const counts = new Map<ProjectColor, number>(PRESET_COLORS.map((c) => [c, 0]));
  for (const p of existing) counts.set(p.color, (counts.get(p.color) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => a[1] - b[1])[0]![0];
}

export async function createProject(input: ProjectInput): Promise<Project> {
  const position = (await db.projects.count());
  return saveRow('projects', { id: uuidv7(), teamId: null, ...input, position, archivedAt: null, updatedAt: '', deletedAt: null });
}

export const updateProject = (p: Project, patch: Partial<ProjectInput>) => saveRow('projects', { ...p, ...patch });
export const archiveProject = (p: Project) => saveRow('projects', { ...p, archivedAt: new Date().toISOString() });
export const unarchiveProject = (p: Project) => saveRow('projects', { ...p, archivedAt: null });

/** Soft-delete the project and everything under it so tombstones propagate through sync (8j "Deletes 41 sessions"). */
export async function deleteProject(p: Project) {
  const [tasks, sessions] = await Promise.all([db.tasks.where('projectId').equals(p.id).toArray(), db.sessions.where('projectId').equals(p.id).toArray()]);
  await db.transaction('rw', [db.projects, db.tasks, db.sessions, db.outbox], async () => {
    for (const t of tasks.filter(notDeleted)) await deleteRow('tasks', t);
    for (const s of sessions.filter(notDeleted)) await deleteRow('sessions', s);
    await deleteRow('projects', p);
  });
  return { tasks: tasks.length, sessions: sessions.length, seconds: sessions.filter(notDeleted).reduce((a, s) => a + s.durationSec, 0) };
}
